import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "@/utils/appsettings";

// Mirrors src/services/connector_registry.py. One request drives the whole Connectors
// screen, so adding a connector on the server needs no change here.

export type ConnectorKey = string;
export type ConnectorScope = "account" | "project";
export type ToolPermission = "always" | "ask" | "never";

export interface ConnectorTool {
  name: string;
  title: string;
  description: string;
  kind: "read" | "write";
  defaultPermission: ToolPermission;
  permission: ToolPermission;
  isDefault: boolean;
}

export interface Connector {
  key: ConnectorKey;
  name: string;
  summary: string;
  docsHint: string;
  scope: ConnectorScope;
  beta: boolean;
  connected: boolean;
  detail: string;
  accountLabel: string;
  warnings: string[];
  metadata: Record<string, unknown>;
  // Server-driven presentation and behaviour. Optional so a stale payload still renders.
  provider?: string;
  authKind?: "oauth2" | "api_key" | "managed" | "credentials";
  icon?: string;
  ctaLabel?: string;
  categories?: string[];
  planFeature?: string;
  workspaceGoverned?: boolean;
  usesGenericOauth?: boolean;
  available?: boolean;
  tools?: ConnectorTool[];
  // Every account connected for this connector. `accountLabel` names only one of them, which was
  // accurate while a user could hold a single account and misleading once two were allowed.
  accounts?: ConnectorAccount[];
  // Non-empty means this connector is configured by typing credentials, not by an OAuth grant.
  credentialFields?: CredentialField[];
}

export interface ConnectorAccount {
  id: number;
  connectionId: string;
  label: string;
  email: string;
  usable: boolean;
  needsReconnect: boolean;
  isDefault: boolean;
}

export interface CredentialField {
  name: string;
  label: string;
  kind: "text" | "password" | "number" | "select";
  placeholder: string;
  helpText: string;
  required: boolean;
  secret: boolean;
  default: string;
  options: string[];
}

export interface ConnectorGroup {
  category: string;
  label: string;
  connectors: Connector[];
}

export interface ConnectorCategory {
  key: string;
  label: string;
  count: number;
}

export interface ConnectorsPayload {
  projectId: string | null;
  workspaceId?: number | null;
  connectedCount: number;
  totalCount: number;
  connected: ConnectorKey[];
  groups: ConnectorGroup[];
  connectors?: Connector[];
  categories?: ConnectorCategory[];
  isWorkspaceAdmin?: boolean;
}

// Empty on every branch: the server's HIDDEN_CONNECTORS env var decides, omitting held-back connectors from /connectors and /connectors/project/{id}.
export const HIDDEN_CONNECTORS = new Set<ConnectorKey>([]);

export const isConnectorHidden = (key: ConnectorKey) => HIDDEN_CONNECTORS.has(key);

const flatten = (groups: ConnectorGroup[]): Connector[] => {
  const seen = new Set<string>();
  return groups.flatMap((g) =>
    g.connectors.filter((c) => {
      if (seen.has(c.key)) return false;
      seen.add(c.key);
      return true;
    }),
  );
};

// Removes the hidden connectors and reconciles the numbers rendered beside the list — header
// total, connected count, per-category counts — so they match the rows that survive the filter.
const stripHidden = (payload: ConnectorsPayload | null): ConnectorsPayload | null => {
  if (!payload) return null;

  const isHidden = isConnectorHidden;
  const originalFlat = payload.connectors?.length
    ? payload.connectors
    : flatten(payload.groups ?? []);
  const removed = originalFlat.filter((c) => isHidden(c.key));
  if (removed.length === 0) return payload;

  const groups = (payload.groups ?? [])
    .map((g) => ({ ...g, connectors: g.connectors.filter((c) => !isHidden(c.key)) }))
    .filter((g) => g.connectors.length > 0);
  const connected = (payload.connected ?? []).filter((key) => !isHidden(key));
  const removedConnected = (payload.connected ?? []).filter(isHidden).length;

  // Categories keep the server's labels and order; only their counts move
  const removedPerCategory = new Map<string, number>();
  for (const connector of removed) {
    for (const cat of connector.categories ?? []) {
      removedPerCategory.set(cat, (removedPerCategory.get(cat) ?? 0) + 1);
    }
  }
  const categories = payload.categories
    ?.map((c) => ({ ...c, count: Math.max(0, c.count - (removedPerCategory.get(c.key) ?? 0)) }))
    .filter((c) => c.count > 0);

  return {
    ...payload,
    connectors: payload.connectors?.filter((c) => !isHidden(c.key)),
    groups,
    connected,
    connectedCount: Math.max(0, payload.connectedCount - removedConnected),
    totalCount: Math.max(0, payload.totalCount - removed.length),
    ...(categories ? { categories } : {}),
  };
};

const errorMessage = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: { error?: { message?: string } } } } })
    ?.response?.data?.detail?.error?.message;
  return detail || fallback;
};

export const useConnectors = (projectId?: string) => {
  const [data, setData] = useState<ConnectorsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/connectors", {
        params: projectId ? { project_id: projectId } : undefined,
      });
      setData(stripHidden(res.data?.data ?? null));
    } catch (err) {
      setError(errorMessage(err, "Could not load your connectors."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Flat list of every connector.
   *
   * Prefers the server's flat `connectors`, falling back to flattening `groups`. The fallback
   * matters because a connector can sit in more than one category: flattening the grouped shape
   * and de-duplicating is what keeps it from appearing twice.
   */
  const connectors = useMemo<Connector[]>(() => {
    if (data?.connectors?.length) return data.connectors;
    const seen = new Set<string>();
    return (data?.groups ?? []).flatMap((g) =>
      g.connectors.filter((c) => {
        if (seen.has(c.key)) return false;
        seen.add(c.key);
        return true;
      }),
    );
  }, [data]);

  /** Categories with counts, derived from the flat list when the server does not send them. */
  const categories = useMemo<ConnectorCategory[]>(() => {
    if (data?.categories?.length) return data.categories;
    const counts = new Map<string, number>();
    for (const c of connectors) {
      for (const cat of c.categories ?? []) counts.set(cat, (counts.get(cat) ?? 0) + 1);
    }
    return [...counts.entries()].map(([key, count]) => ({ key, label: key, count }));
  }, [data, connectors]);

  /** Flat lookup for callers that need one connector rather than the grouped view. */
  const byKey = useCallback(
    (key: ConnectorKey): Connector | undefined => connectors.find((c) => c.key === key),
    [connectors],
  );

  return { data, connectors, categories, loading, error, refresh, byKey };
};

/**
 * Writes to a connector's settings.
 *
 * Kept out of `useConnectors` so the read path stays a single request with no mutation state
 * tangled into it. Both calls return the server's own view, so the caller refreshes rather than
 * guessing what the new state is — the permission the server resolves is the one that governs the
 * agent, and a locally-optimistic value could disagree with it.
 */
export const useConnectorSettings = () => {
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setToolPermissions = useCallback(
    async (connectorKey: string, permissions: Record<string, ToolPermission | null>) => {
      setSaving(connectorKey);
      setError(null);
      try {
        const res = await axiosInstance.put(`/connectors/${connectorKey}/permissions`, {
          permissions,
        });
        return (res.data?.data?.tools ?? null) as ConnectorTool[] | null;
      } catch (err) {
        setError(errorMessage(err, "Could not save those permissions."));
        return null;
      } finally {
        setSaving(null);
      }
    },
    [],
  );

  const setWorkspaceEnabled = useCallback(
    async (connectorKey: string, enabled: boolean, workspaceId?: number | null) => {
      setSaving(connectorKey);
      setError(null);
      try {
        await axiosInstance.patch(`/connectors/${connectorKey}/workspace`, {
          enabled,
          workspace_id: workspaceId ?? undefined,
        });
        return true;
      } catch (err) {
        setError(errorMessage(err, "Could not change that for your workspace."));
        return false;
      } finally {
        setSaving(null);
      }
    },
    [],
  );

  return { saving, error, setToolPermissions, setWorkspaceEnabled };
};

// A popup that is never closed must not leave a timer running: an uncleared interval keeps polling
// every half second for the life of the tab, and in Node it keeps the process alive outright —
// which is how this first showed up, as a test run that finished its tests and then never exited.
const POPUP_POLL_MS = 500;
const POPUP_MAX_WAIT_MS = 5 * 60 * 1000;

const waitForPopupClose = (popup: Window): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        resolve(true);
        return;
      }
      // Give up rather than poll forever; the user can retry from the same dialog
      if (Date.now() - started > POPUP_MAX_WAIT_MS) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, POPUP_POLL_MS);
  });

/**
 * Runs the generic OAuth connect for one connector.
 *
 * Shared by the Connectors screen and the mid-build connect card, because the flow is identical
 * and a second copy is a second place for the pop-up handling to drift.
 *
 * Waits by polling `popup.closed` rather than listening for postMessage: the callback page is
 * served from the API host, so its message origin never matches this one and a strict origin check
 * would silently drop it.
 */
export const useConnectorConnect = () => {
  const [connecting, setConnecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async (connectorKey: string, connectorName?: string) => {
    const label = connectorName || connectorKey;
    setConnecting(connectorKey);
    setError(null);
    try {
      const res = await axiosInstance.post(`/connectors/${connectorKey}/authorize`, {});
      const url = res.data?.data?.authorizationUrl;
      if (!url) throw new Error("no authorization url");

      const popup = window.open(url, "joylo-connector-oauth", "width=560,height=720");
      if (!popup) {
        setError(
          `Your browser blocked the ${label} sign-in window. Allow pop-ups for this site and try again.`,
        );
        return false;
      }

      const closed = await waitForPopupClose(popup);
      if (!closed) {
        setError(
          `The ${label} window is still open. Finish signing in there, or close it and try again.`,
        );
        return false;
      }
      return true;
    } catch (err) {
      setError(errorMessage(err, `Could not start the ${label} connection.`));
      return false;
    } finally {
      setConnecting(null);
    }
  }, []);

  // Stable identity: an inline arrow here is a new function every render, and callers put this in
  // a useEffect dependency array — which turned the Connectors dialog into an infinite render loop
  const clearError = useCallback(() => setError(null), []);

  return { connect, connecting, error, clearError };
};
